'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Eye, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

export default function GallerySection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    
    const galleryImages = [
        {
            id: 1,
            before: '/images/treatments/dental-veneers/gallery-1-before.webp',
            after: '/images/treatments/dental-veneers/gallery-1-after.webp',
            category: t('gallery.veneerTypes.porcelain'),
            description: t('gallery.transformations.complete')
        },
        {
            id: 2,
            before: '/images/treatments/dental-veneers/gallery-2-before.webp',
            after: '/images/treatments/dental-veneers/gallery-2-after.webp',
            category: t('gallery.veneerTypes.emax'),
            description: t('gallery.transformations.emax')
        },
        {
            id: 3,
            before: '/images/treatments/dental-veneers/gallery-3-before.webp',
            after: '/images/treatments/dental-veneers/gallery-3-after.webp',
            category: t('gallery.veneerTypes.composite'),
            description: t('gallery.transformations.composite')
        },
        {
            id: 4,
            before: '/images/treatments/dental-veneers/gallery-4-before.webp',
            after: '/images/treatments/dental-veneers/gallery-4-after.webp',
            category: t('gallery.veneerTypes.lumineers'),
            description: t('gallery.transformations.lumineers')
        },
        {
            id: 5,
            before: '/images/treatments/dental-veneers/gallery-5-before.webp',
            after: '/images/treatments/dental-veneers/gallery-5-after.webp',
            category: t('gallery.veneerTypes.porcelain'),
            description: t('gallery.transformations.discoloration')
        },
        {
            id: 6,
            before: '/images/treatments/dental-veneers/gallery-6-before.webp',
            after: '/images/treatments/dental-veneers/gallery-6-after.webp',
            category: t('gallery.veneerTypes.emax'),
            description: t('gallery.transformations.gapClosure')
        },
        {
            id: 7,
            before: '/images/treatments/dental-veneers/gallery-7-before.webp',
            after: '/images/treatments/dental-veneers/gallery-7-after.webp',
            category: t('gallery.veneerTypes.lumineers'),
            description: t('gallery.transformations.chipped')
        },
        {
            id: 8,
            before: '/images/treatments/dental-veneers/gallery-8-before.webp',
            after: '/images/treatments/dental-veneers/gallery-8-after.webp',
            category: t('gallery.veneerTypes.composite'),
            description: t('gallery.transformations.quick')
        }
    ];
    const [selectedImage, setSelectedImage] = useState<number | null>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set(titleRef.current, { opacity: 0, y: 50 });

            if (gridRef.current?.children) {
                gsap.set(gridRef.current.children, { opacity: 0, y: 50, scale: 0.9 });
            }

            // Title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Grid animation
            if (gridRef.current?.children) {
                gsap.to(gridRef.current.children, {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    stagger: 0.1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: gridRef.current,
                        start: "top 70%",
                        end: "bottom 30%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const nextImage = () => {
        if (selectedImage !== null) {
            const nextIndex = (selectedImage + 1) % galleryImages.length;
            setSelectedImage(nextIndex);
        }
    };

    const prevImage = () => {
        if (selectedImage !== null) {
            const prevIndex = (selectedImage - 1 + galleryImages.length) % galleryImages.length;
            setSelectedImage(prevIndex);
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'Porcelain Veneers':
                return 'bg-blue-600 text-white';
            case 'E-Max Veneers':
                return 'bg-purple-600 text-white';
            case 'Composite Veneers':
                return 'bg-green-600 text-white';
            case 'Lumineers':
                return 'bg-gold text-white';
            default:
                return 'bg-gray-600 text-white';
        }
    };

    return (
        <section
            id="gallery"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                    >
                        {t('gallery.title')}
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
                        {t('gallery.subtitle')}
                    </p>
                </div>

                {/* Gallery Grid */}
                <div ref={gridRef} className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {galleryImages.map((image, index) => (
                        <Dialog key={image.id}>
                            <DialogTrigger asChild>
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ duration: 0.3 }}
                                    className="relative group cursor-pointer"
                                    onClick={() => setSelectedImage(index)}
                                >
                                    <div className="aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-4 border-transparent group-hover:border-gold/50">
                                        <div className="relative w-full h-full">
                                            {/* Side-by-side Before / After */}
                                            <div className="grid grid-cols-2 w-full h-full">
                                                <div className="relative overflow-hidden">
                                                    <OptimizedImage
                                                        src={image.before}
                                                        alt={`Before - ${image.description}`}
                                                        width={300}
                                                        height={300}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <div className="absolute top-3 left-3 bg-red-600/90 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                                                        {t('gallery.before')}
                                                    </div>
                                                </div>
                                                <div className="relative overflow-hidden">
                                                    <OptimizedImage
                                                        src={image.after}
                                                        alt={`After - ${image.description}`}
                                                        width={300}
                                                        height={300}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <div className="absolute top-3 right-3 bg-green-600/90 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                                                        {t('gallery.after')}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Center divider */}
                                            <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px bg-white/60 -translate-x-1/2" />

                                            {/* Hover overlay → "view larger" */}
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                                                <div className={cn(
                                                    "opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white",
                                                    isRTL && "flex-row-reverse"
                                                )}>
                                                    <Eye className="w-6 h-6" />
                                                    <span className={cn(
                                                        "font-medium",
                                                        isRTL && "font-arabic"
                                                    )}>{t('gallery.viewLarger')}</span>
                                                </div>
                                            </div>

                                            {/* Category Badge */}
                                            <div className={`absolute bottom-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(image.category)}`}>
                                                {image.category.replace(' Veneers', '')}
                                            </div>

                                            {/* Description strip */}
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-3 px-4">
                                                <p className="text-white text-sm font-medium line-clamp-2 pr-20">
                                                    {image.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </DialogTrigger>

                            {/* Lightbox Dialog */}
                            <DialogContent className="max-w-4xl w-full p-0 bg-transparent border-none">
                                <div className="relative">
                                    <div className="aspect-video rounded-2xl overflow-hidden bg-black">
                                        <div className="grid grid-cols-2 h-full">
                                            {/* Before Image */}
                                            <div className="relative">
                                                <OptimizedImage
                                                    src={image.before}
                                                    alt={`Before - ${image.description}`}
                                                    width={600}
                                                    height={400}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                    {t('gallery.before')}
                                                </div>
                                            </div>

                                            {/* After Image */}
                                            <div className="relative">
                                                <OptimizedImage
                                                    src={image.after}
                                                    alt={`After - ${image.description}`}
                                                    width={600}
                                                    height={400}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                    {t('gallery.after')}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Divider Line */}
                                        <div className="absolute top-0 bottom-0 left-1/2 w-1 bg-white/50 transform -translate-x-1/2"></div>
                                    </div>

                                    {/* Navigation */}
                                    <button
                                        onClick={prevImage}
                                        aria-label="Previous image"
                                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
                                    >
                                        <ChevronLeft className="w-6 h-6" />
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        aria-label="Next image"
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
                                    >
                                        <ChevronRight className="w-6 h-6" />
                                    </button>

                                    {/* Info Panel */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-2xl">
                                        <div className="text-white">
                                            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${getCategoryColor(image.category)}`}>
                                                {image.category}
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{image.description}</h3>
                                            <div className={cn(
                                                "flex items-center gap-2 text-sm opacity-80",
                                                isRTL && "flex-row-reverse"
                                            )}>
                                                <Sparkles className="w-4 h-4" />
                                                <span className={cn(isRTL && "font-arabic")}>{t('gallery.brand')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    ))}
                </div>
            </div>
        </section>
    );
} 
